import React from 'react';
import { StyleSheet, View } from 'react-native';

import Text from '../Text';
import Button from '../Button';
import { Ionicons } from '../Icons';
import { useStyles } from '~/hooks';
import { FONT_SIZE_M, FONT_SIZE_S } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    icon?: React.ComponentProps<typeof Ionicons>['name'];
    // Translated via the core Text component (i18n keys or plain text).
    title: string;
    body?: string;
    ctaLabel?: string;
    onCtaPress?: () => void;
};

// Centered empty/error state with an optional call to action.
export default function EmptyState({ icon, title, body, ctaLabel, onCtaPress }: Props) {
    const styles = useStyles(stylesGenerator);
    return (
        <View style={styles.container}>
            {icon ? <Ionicons name={icon} size={scaleSize(36)} style={styles.icon} /> : null}
            <Text fontType="LATO_BOLD" style={styles.title}>
                {title}
            </Text>
            {body ? <Text style={styles.body}>{body}</Text> : null}
            {ctaLabel && onCtaPress ? (
                <View style={styles.ctaWrap}>
                    <Button title={ctaLabel} onPress={onCtaPress} />
                </View>
            ) : null}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            alignItems: 'center',
            justifyContent: 'center',
            paddingVertical: scaleSize(32),
            paddingHorizontal: scaleSize(24),
        },
        icon: {
            color: theme.textSecondary,
            marginBottom: scaleSize(10),
        },
        title: {
            fontSize: FONT_SIZE_M,
            color: theme.text,
            textAlign: 'center',
        },
        body: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            textAlign: 'center',
            marginTop: scaleSize(6),
        },
        // width + maxWidth (not alignSelf: 'stretch') so the parent's alignItems: 'center' can
        // centre it: a stretched child capped by maxWidth is pinned to the cross-axis start (left)
        // instead of centred.
        ctaWrap: {
            marginTop: scaleSize(16),
            width: '100%',
            maxWidth: scaleSize(280),
        },
    });
